import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WebSpeechApiService } from './web-speech-api.service';
import { PlayerService } from './player.service';
import { UserVideoService } from './user-video.service';
import { CallService } from 'src/app/services/learner/call.service';
import { AppConstants } from 'src/app/app-constants';
import { DeviceSettingsService } from './device-settings.service';
import { ICallPractice } from 'src/app/interfaces/icall-practice';
import { ICallConversational } from 'src/app/interfaces/icall-conversational';
import { TranscriptCorrections } from 'src/app/utils/transcript-corrector';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { RealtimeService } from './realtime/realtime.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { BehaviorSubject, Observable, of, tap, catchError, throwError, map, from } from 'rxjs';
import { CallStoreService } from './call-store/call-store.service';
import { ImagePreloaderService } from 'src/app/services/learner/image-preloader/image-preloader.service';
import { IPreloadedImageResult } from 'src/app/interfaces/ipreloaded-image-result';
import { MediaStreamService } from './media-stream/media-stream.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { SentryService } from 'src/app/services/logger/sentry.service';
import { CallFlagService } from 'src/app/services/learner/call-flag/call-flag.service';

@Injectable({
  providedIn: 'root',
})
export class ControlService {
  webSpeechApiService: WebSpeechApiService | undefined;
  playerService: PlayerService | undefined;
  userVideoService: UserVideoService | undefined;
  deviceSettingsService: DeviceSettingsService | undefined;
  realtimeService: RealtimeService | undefined;
  public advanceTypeQueryParam: string | null = null;
  public practiceCallUuid: string = '';
  moduleAssignmentUuid: string = '';
  callUuid: string = '';
  practiceType: string = '';
  callType: string = '';
  topClips: number | undefined = undefined;
  callConversational: ICallConversational | undefined = undefined;
  interactionRequirement: number = 4;
  interactionCount: number = 0;

  // controls
  isMicOnBeforePause: boolean = true;
  public isVideoOn: boolean = true;
  isVideoOnBeforePause: boolean = true;
  isEndCallTriggered: boolean = false;
  isVisualAidCall: boolean = false;
  isRealTimeCall: boolean = false;
  callThumbnail: string = '';
  pauseEnabled: boolean = false;
  isResumeInProgress: boolean = false;
  isResumeMessageInProgress: boolean = false;
  userInactivity: boolean = false;
  resetUserInactivity() { this.userInactivity = false; }
  isCallTypeResolved: boolean = false;

  // captions button
  isCaptionsOn: boolean = true;

  // timer
  public timer: string = '00:00';
  timerInterval: any;
  hour: number = 0;
  minute: number = 0;
  second: number = 0;

  // validate audio
  intervalSpeechDetected: any;

  // display
  initials: string = '';
  displayTour: boolean = false;
  displayTourHint: boolean = false;

  isEndCallRequestOnProcess: boolean = false;
  isEndCallError: boolean = false;
  isNetworkError: boolean = false;
  onStartCallComplete: (() => void) | null = null;

  transcriptChanges: TranscriptCorrections = new TranscriptCorrections();
  private isCallTypeResolvedSubject = new BehaviorSubject<boolean>(false);
  isCallTypeResolved$ = this.isCallTypeResolvedSubject.asObservable();

  constructor(
    private router: Router,
    private callService: CallService,
    private amplitudeService: AmplitudeService,
    private callStore: CallStoreService,
    private imagePreloaderService: ImagePreloaderService,
    private mediaStreamService: MediaStreamService,
    private logger: LoggerService,
    private sentryService: SentryService,
    private callFlagService: CallFlagService
  ) { }

  setWebSpeechApiService(webSpeechApiService: WebSpeechApiService) {
    this.webSpeechApiService = webSpeechApiService;
  }

  setPlayerService(playerService: PlayerService) {
    this.playerService = playerService;
  }

  setUserVideoService(userVideoService: UserVideoService) {
    this.userVideoService = userVideoService;
  }

  setDeviceSettingsService(deviceSettingsService: DeviceSettingsService) {
    this.deviceSettingsService = deviceSettingsService;
  }

  setRealtimeService(realtimeService: RealtimeService) {
    this.realtimeService = realtimeService;
  }

  togglePause(allowUnpause: boolean = true, allowPause: boolean = true): void {
    const isCurrentlyPaused = this.callStore.getIsPaused();
    const pause = !isCurrentlyPaused; // true if pausing, false if unpausing

    this.logger.log('[CALL] toggle pause', {
      pause: pause,
    });

    // handle pause for real time call
    const isRealTimeCall = this.isRealTimeCall;
    if (isRealTimeCall) {
      this.realtimeService?.togglePause();
      return;
    }

    if (this.playerService?.videoHTMLElement) {
      // unpause call simulation
      if (this.callStore.getIsPaused() && allowUnpause) {
        this.unpauseCall();
        return;
      }
      // pause call simulation
      if (!this.callStore.getIsPaused() && allowPause) {
        this.pauseCall();
      }
    }
  }

  pauseSimulation(allowPause: boolean = true): void {
    if (this.isRealTimeCall || this.playerService?.videoHTMLElement) {
      // pause call simulation
      if (!this.callStore.getIsPaused() && allowPause) {
        this.pauseCall();
      }
    }
  }

  unpauseSimulation(allowUnpause: boolean = true): void {
    if (this.playerService?.videoHTMLElement) {
      // unpause call simulation
      if (this.callStore.getIsPaused() && allowUnpause) {
        this.unpauseCall();
        return;
      }
    }
  }

  async unpauseCall(): Promise<void> {
    this.logger.log('[CALL] control - unpauseCall');
    if (this.isRealTimeCall) {
      this.realtimeService?.unpauseCall();
      return;
    }
    this.playerService?.videoHTMLElement?.play();
    this.callStore.setIsMicOn(this.isMicOnBeforePause);
    this.isVideoOn = this.isVideoOnBeforePause;
    this.callStore.setIsPaused(false);
    if (
      this.callStore.getIsMicOn() &&
      !this.callStore.getSkipConnectTranscriber()
    ) {
      this.deviceSettingsService?.startAudioLevelDetection();
      await this.playerService?.transcriptCallCheck();
    }
    if (this.isVideoOn) {
      this.userVideoService?.displayUserVideo();
    }
    // This will prevent auto pause after 5 minutes of inactivity after a long pause of simulation
    if (this.playerService) {
      this.callStore.setLastMessageTimeStamp(new Date());
    }
  }

  pauseCall(): void {
    this.playerService?.videoHTMLElement?.pause();
    this.isMicOnBeforePause = this.callStore.getIsMicOn();
    this.isVideoOnBeforePause = this.isVideoOn;
    this.playerService?.transcriptCallCheck(true);
    this.userVideoService?.stopUserVideo();
    this.deviceSettingsService?.stopAudioLevelDetection();
    this.callStore.setIsPaused(true);
    if (this.isRealTimeCall) {
      this.realtimeService?.pauseCall();
      return;
    }
    this.isVideoOn = false;
    this.callStore.setIsMicOn(false);
    this.playerService?.pauseAssembly();
    clearInterval(this.intervalSpeechDetected);
  }

  toggleMic(): void {
    const isMicOn = this.callStore.getIsMicOn();
    const turn = isMicOn ? 'off' : 'on';

    this.logger.log('[CALL] toggle mic', {
      turn: turn,
    });

    // handle mic toggle for real time call
    if (this.isRealTimeCall && this.realtimeService) {
      this.callStore.setIsMicOn(!isMicOn);
      this.callStore.setUserMicPreference(!isMicOn);
      this.realtimeService.toggleMic(this.callStore.getIsMicOn());
      return;
    }

    if (!this.callStore.getIsPaused()) {
      this.callStore.setIsMicOn(!this.callStore.getIsMicOn());
      this.callStore.setUserMicPreference(this.callStore.getIsMicOn());
      if (this.callStore.getIsMicOn()) {
        this.deviceSettingsService?.startAudioLevelDetection();
        this.playerService?.transcriptCallCheck(false);
      } else {
        this.deviceSettingsService?.stopAudioLevelDetection();
        this.playerService?.transcriptCallCheck(true);
      }
    }
  }

  toggleVideo(): void {
    if (!this.callStore.getIsPaused()) {
      const turn = this.isVideoOn ? 'off' : 'on';

      this.logger.log('[CALL] toggle video', {
        turn: turn,
      });

      if (this.isVideoOn) {
        this.isVideoOn = false;
        this.mediaStreamService.stopVideoStream();
        this.realtimeService?.stopUserVideo();
      } else {
        this.isVideoOn = true;
        this.userVideoService?.displayUserVideo();
      }
      this.realtimeService?.setNativeCameraEnabled(this.isVideoOn);
    }
  }

  async endCall(forceEndcall: boolean = false): Promise<void> {
    this.logger.log('[CALL] end call', {
      forceEndcall,
      isRealTimeCall: this.isRealTimeCall,
    });
    if (this.isRealTimeCall) {
      console.log('User clicked "End Call" button on footer');
      this.realtimeService?.endRealtimeConversation();
      return;
    }
    this.playerService?.pauseAssembly();
    this.mediaStreamService.cleanupVideoStream();

    this.isEndCallTriggered = true;
    if (this.playerService) {
      this.playerService.videoHTMLElement?.pause();
      const isConversational = this.isConversational();
      if (!forceEndcall) {
        const isSpeechTranscriptEmpty =
          this.callStore.getSpeechTranscript().trim().length === 0 &&
          this.callStore.getSpeechTranscriptPartial().trim().length === 0;
        if (!isSpeechTranscriptEmpty) {
          // conversational call validation
          const isRequirementMet =
            this.interactionCount + 1 >= this.interactionRequirement;
          if (isConversational && !isRequirementMet) {
            console.log(`Only pause call conversational`);
            return;
          }

          // guided call validation
          const isLastQuestionClip = this.playerService.validateCallComplete();
          if (!isConversational && !isLastQuestionClip) {
            console.log(`Only pause call guided`);
            return;
          }
        }
      }

      this.playerService.setFinalAnswer();
      const isFinalAnswer = this.playerService.finalAnswer.trim().length > 0;
      // conversational end call
      if (isConversational) {
        if (isFinalAnswer) {
          this.callStore.setIsSendingAnswer(true);
          this.playerService.sendAnswerConversational(false);
        }
        this.callStore.setIsCallComplete(
          this.interactionCount >= this.interactionRequirement
        );
      } else {
        // guided end call
        const lastQuestionUuidSent = this.playerService.lastQuestionUuidSent;
        const currentClip =
          this.playerService.callClips[this.playerService.currentClipIndex];
        this.playerService.finalQuestionUuid = currentClip.questionUuid;
        const questionUuid = this.playerService.finalQuestionUuid;
        if (
          questionUuid &&
          lastQuestionUuidSent !== questionUuid &&
          isFinalAnswer
        ) {
          this.playerService.sendAnswer();
          this.callStore.setIsCallComplete(
            this.playerService.validateCallComplete()
          );
        }
      }

      if (this.callStore.getIsCallComplete()) {
        //log transcript changes
        console.log(this.callStore.getTranscriptChanges());

        this.callStore.setDisplayPostCall(true);
        this.sendRequestToEndCall();
        await this.resetRecognitionControls();
      } else {
        this.isEndCallTriggered = true;
        this.playerService.videoHTMLElement?.pause();
        this.pauseCall();
      }
    }
  }

  async endCallVisualAid(): Promise<void> {
    this.mediaStreamService.cleanupVideoStream();
    this.isEndCallTriggered = true;
    if (this.playerService) {
      // Pause the video and stop audio processing
      this.playerService.videoHTMLElement?.pause();
      this.playerService.pauseAssembly();

      // Ensure we're dealing with a guided call (visual aid assumption)
      if (this.callType !== AppConstants.CALL_TYPE_GUIDED) {
        console.error('endCallVisualAid is only applicable for guided calls');
        return;
      }

      // Get the visual aid answer from PlayerService
      const visualAidAnswer = this.playerService.getAnswer();
      const currentClip =
        this.playerService.callClips[this.playerService.currentClipIndex];
      this.playerService.finalQuestionUuid = currentClip?.questionUuid || null;

      // If there's a visual aid answer, send it before ending the call
      if (visualAidAnswer.length > 0 && this.playerService.finalQuestionUuid) {
        this.callStore.setIsSendingAnswer(true);
        this.playerService.finalAnswer = visualAidAnswer;

        // Send the visual aid answer
        this.playerService.sendVisualAidAnswer(visualAidAnswer);

        // Wait briefly to ensure the answer is sent before proceeding
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Mark the call as complete if it's the last question
      const callComplete = this.playerService.validateCallComplete();
      this.callStore.setIsCallComplete(callComplete);
      if (callComplete) {
        // Log transcript changes if any
        console.log(this.callStore.getTranscriptChanges());

        this.callStore.setDisplayPostCall(true);
        this.sendRequestToEndCall();
        await this.resetRecognitionControls();
        await this.playerService.stopAssembly();
      } else {
        this.pauseCall();
      }
    }
  }

  sendRequestToEndCall(): void {
    if (!this.practiceCallUuid) {
      throw new Error('practiceCallUuid is null');
    }
    this.sentryService.endCallSession();

    let chatHistory = this.playerService?.chatHistory || '';
    if (this.isRealTimeCall) {
      chatHistory = localStorage.getItem('convoHistory') || '';
    }

    this.isEndCallRequestOnProcess = true;
    this.isEndCallError = false;
    this.isNetworkError = false;
    this.callService
      .endCall(
        this.practiceCallUuid,
        chatHistory,
        this.callStore.getTranscriptChanges().getCorrections()
      )
      .subscribe({
        next: (res: any) => {
          console.log(res);
          this.isEndCallRequestOnProcess = false;
        },
        error: (err) => {
          this.isEndCallError = true;
          this.isNetworkError = navigator.onLine ? false : true;
          this.logger.error('[CALL] endCall', err, {
            err,
          });
        },
      });
  }

  async resetPlayer(): Promise<void> {
    this.logger.log('[CALL] reset player');
    if (this.playerService) {
      this.playerService.currentClipIndex = 0;
      this.callStore.setCurrentView('');
      await this.playerService.stopAssembly();
    }
    await this.resetRecognitionControls();
  }

  async resetRecognitionControls(): Promise<void> {
    this.logger.log('[CALL] reset recognition controls');
    console.log('resetRecognitionControls called');
    this.imagePreloaderService.clearCache();
    if (this.userVideoService) {
      this.userVideoService.stopUserVideo();
    }
    if (this.deviceSettingsService) {
      this.deviceSettingsService.stopAudioLevelDetection();
      this.deviceSettingsService.displayModal = false;
      this.deviceSettingsService.isJoinInProgress = false;
    }
    this.callStore.setIsAudioDeviceSelected(true);
    this.callStore.setSpeechTranscript('');
    this.callStore.setSpeechTranscriptPartial('');
    this.callStore.setRecognizedTranscripts([]);
    this.callStore.setIsContinueButtonBlocked(false);
    this.callStore.setIsSendingAnswer(false);
    this.callStore.setAnswerData(null);
    this.callStore.setReplaceWords([]);
    this.callStore.setPronounceWords([]);
    this.callStore.setCallWords([]);
    this.callStore.setSttWords([]);
    this.callStore.setIsFullScreen(false);
    if (this.playerService) {
      clearInterval(this.playerService.conversationalInterval);
      this.playerService.avatarSpeech.set('');
      this.playerService.lastUserMessageSent = '';
      this.playerService.showLoadingScreen = true;
      this.playerService.lastQuestionUuidSent = '';
      this.playerService.finalAnswer = '';
      this.playerService.resetVideoHTMLElement();
      this.playerService.currentClip = null;
      this.playerService.chatHistory = '';
      this.playerService.practiceCallClips = [];
      this.playerService.isPlayingPreResponse = false;
      this.playerService.isAvatarListening = false;
      this.playerService.isLoopActive = false;
      this.playerService.isWaitingConversationalResponse = false;
      this.playerService.preResponseWasPlayed = false;
      this.playerService.avatarOffsetHeight = 0;
      this.playerService.avatarOffsetWidth = 0;
      this.playerService.canPlayVideo = false;
      this.playerService.isBeginCallDisabled = true;
      this.playerService.answerSentDateTime = null;
      this.playerService.conversationalInterval = null;
      this.playerService.conversationStatusCode = 0;
      this.playerService.shouldHandleResponse = null;
      this.playerService.converseStatus = null;
      this.playerService.triggerViewChange = false;
      this.playerService.loopClipFilename = '';
      this.playerService.stopAssembly();
    }
    this.callStore.setIsLoadingNoResponse(false);
    this.callStore.setIsResumeFromModal(false);
    this.callStore.setResumeFromModalType(null);
    this.callStore.setIsPaused(false);
    this.callStore.setIsMicOn(true);
    this.callStore.setUserMicPreference(true);
    this.callStore.setSkipConnectTranscriber(true);
    this.callStore.setIsAssemblyInitialized(false);
    this.isVideoOn = true;
    this.isEndCallTriggered = false;
    this.interactionCount = 0;
    this.interactionRequirement = 4;
    this.logger.log(`[CALL] interaction count reset: 0/${this.interactionRequirement}`, { interactionCount: 0, interactionCountRequirement: this.interactionRequirement });
    this.advanceTypeQueryParam = null;
    this.callStore.setOpenNoResponseModal(false);
    this.displayTour = false;
    this.displayTourHint = false;
    this.isMicOnBeforePause = true;
    this.isVideoOnBeforePause = true;
    this.isResumeInProgress = false;
    this.isResumeMessageInProgress = false;
    clearInterval(this.intervalSpeechDetected);
    // Reset timer
    clearInterval(this.timerInterval);
    this.timer = '00:00';
    localStorage.setItem('convoHistory', JSON.stringify({ chatHistory: [] }));
    if (this.realtimeService) {
      this.realtimeService.lastQuestionUuidSent = '';
      this.realtimeService.shouldConnectTranscriberOnMicOn = false;
      this.realtimeService.stopKeepAliveTimer('recognition reset');
    }
  }

  viewMyAssignements(): void {
    // Close any open Sentry call-session if the user abandons the call mid-session
    this.sentryService.endCallSession();
    // TODO: fix this on route validation, we should only have one route
    if (localStorage.getItem('roles')?.includes('TeamAdmin')) {
      this.router.navigate(['/team_manager/assignments']);
      return;
    }
    this.router.navigate(['/assignments']);
  }

  private getResultsPath(): string {
    const isGuided = this.callType === AppConstants.CALL_TYPE_GUIDED;
    const isPersonalized = this.callStore.getIsPersonalizedCall();

    if (isGuided) {
      return this.isVisualAidCall
        ? '/results_visual_aid'
        : '/results_guided';
    }

    if (isPersonalized) {
      return '/result_personalized_practice';
    }

    if (this.callType === AppConstants.CALL_TYPE_CONVERSATIONAL) {
      return '/results_conversational';
    }

    return '/results_guided';
  }

  getResultsQueryParams(): { [key: string]: string } {
    const isGuided = this.callType === AppConstants.CALL_TYPE_GUIDED;
    const isPersonalized = this.callStore.getIsPersonalizedCall();

    const queryParams: { [key: string]: string } = {
      practiceCallUuid: this.practiceCallUuid,
    };

    if (isGuided) {
      queryParams['callUuid'] = this.callUuid;
    } else if (this.callType === AppConstants.CALL_TYPE_CONVERSATIONAL || isPersonalized) {
      queryParams['callUuid'] = this.callUuid;
      queryParams['moduleAssignmentUuid'] = this.moduleAssignmentUuid;
    } else {
      queryParams['callUuid'] = this.callUuid;
    }

    return queryParams;
  }

  redirectIfTestSession(): boolean {
    const context = this.callFlagService.consumeTestSession(this.callUuid);
    if (!context) return false;

    this.router.navigate(['/self-serve/simulations', this.callUuid], {
      queryParams: { moduleUuid: context.moduleUuid },
    });
    return true;
  }

  viewResultsPage() {
    if (this.redirectIfTestSession()) return;

    const resultsPath = this.getResultsPath();
    const queryParams = this.getResultsQueryParams();

    const returnUrl = `${resultsPath}?${new URLSearchParams(queryParams).toString()}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));

    this.router.navigate([resultsPath], { queryParams });
  }

  startTimer(): void {
    this.timer = '00:00';
    this.hour = 0;
    this.minute = 0;
    this.second = 0;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      if (!this.callStore.getIsPaused()) {
        this.second++;
        if (this.second === 60) {
          this.second = 0;
          this.minute++;
          if (this.minute === 60) {
            this.minute = 0;
            this.hour++;
          }
        }
        let lblHour =
          this.hour === 0
            ? ''
            : (this.hour < 10 ? '0' + this.hour : this.hour) + ':';
        this.timer =
          lblHour +
          (this.minute < 10 ? '0' + this.minute : this.minute) +
          ':' +
          (this.second < 10 ? '0' + this.second : this.second);
      }
    }, 1000);
  }

  pauseAfterInactivity() {
    const timeInterval: number = 1000 * 60 * 5;
    let loopCounter: number = 0;
    // interval runs every 5 minutes
    const interval = setInterval(() => {
      loopCounter++;
      if (!this.callStore.getIsPaused()) {
        // validate that the call is in practice view
        const isPracticeView =
          this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
        if (isPracticeView) {
          // validate time since last message is greater than 5 minutes
          const lastMessageTimeStamp = this.callStore
            .getLastMessageTimeStamp()
            .getTime();
          const timeSinceLastMessage =
            new Date().getTime() - lastMessageTimeStamp;
          const haveNotReceivedMessageInNMinutes =
            timeSinceLastMessage > timeInterval;
          // validate that the call is not paused
          if (haveNotReceivedMessageInNMinutes) {
            this.logger.log('[CALL] pause after inactivity');
            console.log(
              `pauseAfterInactivity ${loopCounter} loop, last message sent on ${this.callStore.getLastMessageTimeStamp()}`
            );
            this.togglePause();
          }
        } else {
          // if the call is not in practice view, stop the interval
          clearInterval(interval);
        }
      }
    }, timeInterval);
    return interval;
  }

  setAdvanceType(advanceType: string): void {
    if (this.playerService) {
      if (this.advanceTypeQueryParam) {
        // SAFE: fixed internal advanceType value, never free user text
        this.callStore.setAdvanceType(this.advanceTypeQueryParam.toUpperCase());
      } else {
        // SAFE: same, fixed internal enum-like value
        this.callStore.setAdvanceType(advanceType.toUpperCase());
      }
    }
  }

  setQueryParams(
    moduleAssignmentUuid: string,
    callUuid: string,
    practiceType: string,
    callType: string,
    topClips: number | undefined
  ): void {
    this.moduleAssignmentUuid = moduleAssignmentUuid;
    this.callUuid = callUuid;
    this.practiceType = practiceType;
    this.callType = callType;
    if (topClips) this.topClips = topClips;

    this.isCaptionsOn = this.callType === AppConstants.CALL_TYPE_GUIDED;
  }

  isConversational(): boolean {
    return (
      this.callStore.getCall()?.callType ===
      AppConstants.CALL_TYPE_CONVERSATIONAL
    );
  }

  addInteraction(): void {
    this.interactionCount++;
    this.logger.log(`[CALL] interaction count incremented: ${this.interactionCount}/${this.interactionRequirement}`, { interactionCount: this.interactionCount, interactionCountRequirement: this.interactionRequirement });
  }


    startCall(): void {
    if (!this.playerService) return;
    this.playerService.showLoadingScreen = true;

    if (this.isRealTimeCall) {
      this.realtimeService?.startCallRequest();
      this.callStore.setPersonalizedLaunchPayload(null);
      return;
    }

    this.callService
      .startCall(
        this.moduleAssignmentUuid,
        this.callUuid,
        this.practiceType,
        this.callType,
        this.callStore.getAcknowledgementSessionToken() // Pass acknowledgement token for certification
      )
      .subscribe({
        next: (res: ICallPractice) => {
          console.log({ startCall: res });
          this.callStore.setCall(res);
          this.callStore.setReplaceWords(res?.replaceWords || []);
          this.callStore.setCallWords(res?.callWords || []);
          this.callStore.setSttWords(res?.sttWords || []);

          const baseUrl = res?.visualAid?.baseUrl || '';
          if (baseUrl !== '') {
            // Get visual aid indices from call clips
            const guidedClips = res?.callGuidedClips || [];
            const indices =
              this.imagePreloaderService.getVisualAidIndices(guidedClips);

            if (indices.length === 0) return;
            // Preload all images for the visual aid call
            console.log('Unique Visual Aid Indices:', indices);
            this.imagePreloaderService
              .preloadImages(baseUrl, indices)
              .then((results: IPreloadedImageResult[]) => {
                this.callStore.setVisualAidImages(results);
                console.log(`Successfully preloaded ${results.length} images`);
                // Set the first image URL for display
                if (results.length > 0) {
                  const firstImage = results[0].src;
                  console.log('First preloaded image URL:', firstImage);
                  this.callStore.setVisualAidImageUrl(firstImage);
                }
              })
              .catch((error) => {
                console.error('Error preloading images:', error);
              });
          }

          this.setViewFirstClip();

          // get conversational call data if call is conversational
          const isConversational = this.isConversational();
          if (isConversational) {
            this.startCallConversational();
          }

          // set call data
          this.setAdvanceType(res.advanceType);

          this.callStore.setTranscriptChanges();

          this.practiceCallUuid = res.practiceCallUuid;
          this.callStore.setPracticeCallUuid(this.practiceCallUuid);
          if (res.callGuidedClips && this.playerService) {
            this.playerService.joinAgentSays(res.callGuidedClips);
            this.playerService.setCallClipsHTML();
            if (this.onStartCallComplete) {
              const callback = this.onStartCallComplete;
              this.onStartCallComplete = null;
              callback();
            } else {
              this.deviceSettingsService?.validateDeviceAccess();
            }
            return;
          }
          throw new Error('callClips is null');
        },
        error: (err) => {
          this.logger.error('[CALL] startCall', err, {
            err,
          });

          if (err?.status === 400) {
            this.router.navigate(['assignment'], {
                queryParams: {
                  moduleAssignmentUuid: this.moduleAssignmentUuid,
                  practiceType: this.practiceType,
                },
              });
          }
        },
      });
  }

  setViewFirstClip(): void {
    const call = this.callStore.getCall();
    if (call && this.playerService) {
      if (call.callGuidedClips.length > 0) {
        const view = call.callGuidedClips[0].type;
        // prevent view change if audio is not set for call
        const isAudioSetForCall =
          this.deviceSettingsService?.validateAudioSetForCall();
        if (isAudioSetForCall) {
          this.callStore.setCurrentView(view);
        }
        this.playerService.showLoadingScreen = true;
        if (view !== AppConstants.VIEW_PRE_CALL) {
          this.playerService.hasPreCallClips = false;
          this.playerService.triggerViewChange = true;
        } else {
          this.playerService.hasPreCallClips = true;
          this.playerService.triggerViewChange = false;
        }
      }
    }
  }
  startCallConversational(): void {
    this.callService
      .startCallConversational(this.moduleAssignmentUuid, this.callUuid)
      .subscribe({
        next: (res: ICallConversational) => {
          console.log({ startCallConversational: res });
          this.callConversational = res;
          this.playerService?.getPreResponseClips();
        },
        error: (err) => {
          this.logger.error('[CALL] startCallConversational', err, {
            err,
          });

          if (err?.status === 400) {
            this.router.navigate(['assignment'], {
                queryParams: {
                  moduleAssignmentUuid: this.moduleAssignmentUuid,
                  practiceType: this.practiceType,
                },
              });
          }
        },
      });
  }


  getUserInitials(): void {
    const firstName: string =
      localStorage.getItem('user_first_name')?.trim() || '';
    const lastName: string =
      localStorage.getItem('user_last_name')?.trim() || '';
    this.initials = firstName.charAt(0) + lastName.charAt(0);
    if (this.initials.length === 1) {
      this.initials = 'U';
    }
  }

  toggleCaptions(): void {
    const turn = this.isCaptionsOn ? 'off' : 'on';

    this.logger.log('[CALL] closed captions click', {
      turn: turn,
    });

    this.isCaptionsOn = !this.isCaptionsOn;
    const captions_status = this.isCaptionsOn ? 'Captions on' : 'Captions off';
    let call_type: string = '';

    if (this.callType === AppConstants.CALL_TYPE_GUIDED) {
      call_type = 'Guided';
    } else if (this.callType === AppConstants.CALL_TYPE_CONVERSATIONAL) {
      call_type = 'Conversational';
    }

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_CAPTIONS,
      {
        captions_status,
        call_type,
      }
    );
  }

  clickOpenNoResponse(): void {
    this.callStore.setOpenNoResponseModal(true);
    this.pauseCall();
  }

  storeCallUUID(uuid: string): void {
    // Store the call UUID if it's not already stored.
    let callStarted: string[] = JSON.parse(
      localStorage.getItem('callStarted') || '[]'
    );

    if (!callStarted.includes(uuid)) {
      callStarted.push(uuid);
      localStorage.setItem('callStarted', JSON.stringify(callStarted));
    }
  }

  hasAccessedCall(uuid: string): boolean {
    // Check if the call UUID has already been accessed.
    const callStarted: string[] = JSON.parse(
      localStorage.getItem('callStarted') || '[]'
    );
    return callStarted.includes(uuid);
  }

  shouldDisplayTour(): boolean {
    if (this.isRealTimeCall) {
      return false;
    }

    if (this.isVisualAidCall) {
      return false;
    }

    const uuid = this.callUuid;
    if (!this.hasAccessedCall(uuid)) {
      this.storeCallUUID(uuid);
      console.log('Call accessed for the first time, display tour.');
      return true;
    } else {
      console.log('Call has already been accessed, do not display tour.');
    }
    return false;
  }

  goBackToAssigment(): void {
    if (this.redirectIfTestSession()) return;
    if (this.callStore.getIsPersonalizedCall()) {
      this.goBackToPersonalizedAssignment();
      return;
    }
    const moduleAssignmentUuid = this.moduleAssignmentUuid;
    const practiceType = this.practiceType;
    const messagePath =
      moduleAssignmentUuid && practiceType ? '/assignment' : '/assignments';

    this.logger.log('[CALL] go back to assignment', {
      messagePath: messagePath,
    });

    if (!moduleAssignmentUuid || !practiceType) {
      this.router.navigate(['/assignments']);
      throw new Error(
        `moduleAssignmentUuid [${moduleAssignmentUuid}] or practiceType [${practiceType}] is null`
      );
    }
    this.router.navigate(['/assignment'], {
      queryParams: {
        moduleAssignmentUuid,
        practiceType,
      },
    });
  }

  goBackToPersonalizedAssignment(): void {
    this.logger.log('[CALL] go back to practice assignment', {
      messagePath: '/personalized-practice',
    });

    this.router.navigate(['/personalized-practice']);
  }

  setIsCallTypeResolved(value: boolean): void {
    this.isCallTypeResolvedSubject.next(value);
  }

  get isCallTypeDetected(): boolean {
    return this.isCallTypeResolvedSubject.getValue();
  }

  resetForNewCall(): void {
    // Set during startCallConversational() mid-call — must be cleared for next call
    this.callConversational = undefined;

    // Set in startCall() API response — stale practiceCallUuid from previous call
    this.practiceCallUuid = '';

    // Runtime end-call state — not reset by resetRecognitionControls()
    this.isEndCallRequestOnProcess = false;
    this.isEndCallError = false;
    this.isNetworkError = false;

    // Runtime inactivity flag set during call monitoring
    this.userInactivity = false;

    // Timer sub-fields — startTimer() also resets these but clearing here prevents stale display
    this.hour = 0;
    this.minute = 0;
    this.second = 0;

    this.deviceSettingsService?.resetForNewCall();
    this.userVideoService?.resetForNewCall();

    // NOT reset: isCallTypeResolved / isCallTypeResolvedSubject
    //   call.component.ts:156 resets it in ngOnInit() at the right time.
    //   initCallFlags() (call.component.ts:467) sets it to true BEFORE startCall() fires.
    //   Resetting it here emits false AFTER initCallFlags() already set it true, causing
    //   app-call-practice to unmount and never remount — avatar-audio-realtime disappears from DOM.
    // Note: isRealTimeCall, isVisualAidCall, pauseEnabled, callThumbnail are NOT reset here
    // because call.component.ts resets isRealTimeCall in ngOnInit() and all four are
    // set via initCallFlags() BEFORE startCall() is triggered.
    // Note: callType, callUuid, moduleAssignmentUuid, practiceType, topClips are NOT reset here
    // because setQueryParams() sets them BEFORE startCall() is triggered.
    // Note: Properties already covered by resetRecognitionControls() are not duplicated here.
  }
}
